import React, { useState, useRef } from 'react';
import { Image, Video, X, Upload, AlertCircle } from 'lucide-react';
import { postService } from '../lib/postService';

  // --- PWA Upload Fixer: canvas-based compression + localStorage fallback ---
  const compressImage = (file, maxDim = 800, qualities = [0.8, 0.5, 0.3]) => {
    return new Promise((resolve, reject) => {
      const tempUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(tempUrl);
        for (const q of qualities) {
          try {
            const data = canvas.toDataURL('image/jpeg', q);
            resolve(data);
            return;
          } catch (_) {}
        }
        reject(new Error('All compression qualities failed'));
      };
      img.onerror = () => { URL.revokeObjectURL(tempUrl); reject(new Error('Image load failed')); };
      img.src = tempUrl;
    });
  };
  // --- End PWA Upload Fixer snippet ---


/**
 * UploadComponent
 * Props:
 *  - onUploadSuccess(url: string, type: 'image'|'video'|'model') — called after successful upload
 *  - triggerRef — optional forwarded ref; calling triggerRef.current.open() opens the file dialog
 *  - compact — if true, renders only the preview strip (no standalone upload button)
 */
const UploadComponent = ({ onUploadSuccess, triggerRef, compact = false }) => {
  const [preview, setPreview] = useState(null); // { url, type }
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Expose open() via triggerRef
  if (triggerRef) {
    triggerRef.current = {
      open: () => fileInputRef.current?.click(),
      clear: () => {
        setPreview(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
  }

  const MAX_IMAGE = 20 * 1024 * 1024;   // 20 MB (will be compressed)
  const MAX_VIDEO = 50 * 1024 * 1024;  // 50 MB

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const isVideo = file.type.startsWith('video/');
    const isModel = file.name.endsWith('.glb') || file.name.endsWith('.gltf');
    const type = isModel ? 'model' : isVideo ? 'video' : 'image';
    const limit = isModel ? MAX_VIDEO : isVideo ? MAX_VIDEO : MAX_IMAGE; // Allow models up to 50MB

    if (file.size > limit) {
      setError(`File exceeds ${limit / (1024*1024)} MB limit`);
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreview({ url: localUrl, type });

    setUploading(true);
    try {
      const uploadedUrl = await postService.uploadMedia(file, type);
      onUploadSuccess?.(uploadedUrl, type);
    } catch (err) {
      console.warn('Upload failed, using local object URL as fallback:', err);
      // Still provide the local URL as a fallback so the post can be created
      onUploadSuccess?.(localUrl, isVideo ? 'video' : 'image');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setPreview(null);
    setError(null);
    onUploadSuccess?.(null, null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.glb,.gltf"
        onChange={handleFileChange}
        className="sr-only"
      />

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-rose-400 text-xs mt-1 mb-2 px-1">
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}

      {/* Preview strip */}
      {preview && (
        <div className="relative mt-3 rounded-xl overflow-hidden border border-white/10 group">
          {preview.type === 'video' ? (
            <video
              src={preview.url}
              className="w-full max-h-72 object-cover rounded-xl"
              controls
              muted
            />
          ) : preview.type === 'model' ? (
            <div className="w-full h-48 bg-black/50 flex flex-col items-center justify-center border border-white/10 rounded-xl">
              <span className="material-symbols-outlined text-4xl text-[#00ffcc] mb-2 animate-pulse">3d_rotation</span>
              <span className="text-white/70 text-xs font-mono tracking-widest uppercase">3D Asset Ready</span>
            </div>
          ) : (
            <img
              src={preview.url}
              alt="Upload preview"
              className="w-full max-h-72 object-cover rounded-xl"
            />
          )}

          {/* Uploading overlay */}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-white text-xs font-medium">Uploading…</span>
              </div>
            </div>
          )}

          {/* Clear button */}
          {!uploading && (
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X size={14} />
            </button>
          )}

          {/* Type badge */}
          <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 rounded-full text-[10px] text-white/80 flex items-center gap-1">
            {preview.type === 'video' ? <Video size={10} /> : preview.type === 'model' ? <span className="material-symbols-outlined text-[12px]">view_in_ar</span> : <Image size={10} />}
            {preview.type === 'video' ? 'Video' : preview.type === 'model' ? '3D Model' : 'Photo'}
          </div>
        </div>
      )}

      {/* Standalone upload button (non-compact mode) */}
      {!compact && !preview && (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all text-sm"
        >
          <Upload size={16} />
          <span>Add Photo or Video</span>
          <span className="text-white/30 text-xs ml-1">(5 MB / 50 MB)</span>
        </button>
      )}
    </div>
  );
};

export default UploadComponent;
